import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import type { ChangesPerDayPoint, TimeWindow } from '../types/domain';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from './ui/chart';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

interface Props {
  data: ChangesPerDayPoint[];
  isLoading: boolean;
  isError: boolean;
  windowDays: TimeWindow;
  onWindowChange: (days: TimeWindow) => void;
}

const chartConfig = {
  count: { label: 'Cambios', color: 'var(--chart-1)' },
} satisfies ChartConfig;

const WINDOW_LABELS: Record<TimeWindow, string> = {
  7: 'Últimos 7 días',
  14: 'Últimos 14 días',
  30: 'Últimos 30 días',
};

const WINDOWS: TimeWindow[] = [30, 14, 7];

function formatDay(day: string) {
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) return day;
  return new Date(Date.UTC(year, month - 1, date)).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

export function ChangesPerDayChart({
  data,
  isLoading,
  isError,
  windowDays,
  onWindowChange,
}: Props) {
  const isEmpty = !isLoading && !isError && data.every((p) => p.count === 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 space-y-0 border-b py-5">
        <div className="grid flex-1 gap-1">
          <CardTitle>Cambios por día</CardTitle>
          <CardDescription>{WINDOW_LABELS[windowDays]}</CardDescription>
        </div>
        <Select
          value={String(windowDays)}
          onValueChange={(value) => onWindowChange(Number(value) as TimeWindow)}
        >
          <SelectTrigger className="w-[180px] rounded-lg" aria-label="Seleccionar período">
            <SelectValue placeholder={WINDOW_LABELS[30]} />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            {WINDOWS.map((w) => (
              <SelectItem key={w} value={String(w)}>
                {WINDOW_LABELS[w]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        {isLoading ? (
          <div className="h-[250px] w-full animate-pulse rounded-md bg-muted/40" />
        ) : isError ? (
          <div className="flex h-[250px] items-center justify-center">
            <p className="text-sm text-destructive">Error al cargar los datos del gráfico.</p>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="fillCount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-count)" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="var(--color-count)" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="day"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={formatDay}
              />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={32} />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    labelFormatter={(label) => formatDay(String(label))}
                  />
                }
              />
              <Area
                dataKey="count"
                type="natural"
                fill="url(#fillCount)"
                stroke="var(--color-count)"
              />
            </AreaChart>
          </ChartContainer>
        )}
        {isEmpty && (
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Sin cambios en este período
          </p>
        )}
      </CardContent>
    </Card>
  );
}
